/**
 * 导演台工程状态 store（P0-3a，zustand）
 *
 * 职责边界：这里是**工程数据的唯一可变出口**——所有增删改都经过这里的不变量
 * 检查（id 唯一、fov/缩放钳制、删除角色时机位跟随级联处理）。视口/面板组件
 * 一律不得直接改 project 对象，避免"某处改了、校验器永远没跑过"的漂移。
 *
 * 撤销/重做（P0-10）：project 的每一处变更都经 applyProject 记录历史快照。
 * 连续同目标编辑（拖拽/滑杆的每帧）按 500ms 窗口合并成一步，避免一次拖拽塞进
 * 上百条 undo。选择态（selected 系列与 viewMode）不入历史——撤销只回退工程数据本身。
 */
import { create } from 'zustand';

import {
  clampFov,
  clampUniformScale,
  createEmptyDirectorProject,
  validateDirectorProject,
  type DirectorCamera,
  type DirectorObject,
  type DirectorProject,
  type SceneConfig,
  type Transform,
} from '../../model/director';
import { COALESCE_MS, pushPast, redoStep, undoStep } from './history';

export interface DirectorStore {
  project: DirectorProject;
  /** 多选：视口点选/框选结果；删除类操作只对选中生效 */
  selectedObjectIds: string[];
  /** 面板当前编辑的机位；与"取景机位"(activeCameraId) 分开是有意的——
   *  编辑 B 机位时视口仍应保持 A 机位取景，合并成一个概念会导致改哪看哪的错觉 */
  selectedCameraId: string | null;
  /** 视口模式（P0-5b 决策 5）：free=自由环绕；camera=透过取景机位看 */
  viewMode: 'free' | 'camera';
  /** 逐骨编辑当前选中的骨骼名（P0-6d 路线 B）；null=未选骨（此时对象 gizmo 才生效） */
  selectedBoneName: string | null;
  /** 导出态：为真时视口隐藏所有编辑叠加层（PiP/选中框/gizmo/机位标记/网格），供截干净关键帧图 */
  capturing: boolean;

  /** 撤销/重做历史（project 快照栈）。_lastKey/_lastAt 用于拖拽连续帧合并 */
  _past: DirectorProject[];
  _future: DirectorProject[];
  _lastKey: string | null;
  _lastAt: number;

  setViewMode(mode: 'free' | 'camera'): void;
  selectBone(name: string | null): void;
  setCapturing(v: boolean): void;

  undo(): void;
  redo(): void;

  /** 整体载入工程（刷新恢复/模板套用）。结构不合法直接拒绝，不"尽力解析" */
  setProject(project: DirectorProject): boolean;
  resetProject(): void;

  addObject(obj: DirectorObject): boolean;
  updateObject(
    id: string,
    patch: Partial<Omit<DirectorObject, 'id' | 'transform'>> & {
      transform?: Partial<Transform>;
    },
  ): boolean;
  /** 删除角色/道具。返回是否发生删除 */
  removeObject(id: string): boolean;
  selectObjects(ids: string[]): void;

  addCamera(cam: DirectorCamera): boolean;
  /** 面板选中某机位编辑（null = 取消选中）；与 setActiveCamera（取景）是两个独立动作 */
  selectCamera(id: string | null): boolean;
  updateCamera(
    id: string,
    patch: {
      name?: string;
      position?: DirectorCamera['position'];
      fov?: number;
      target?: DirectorCamera['target'];
    },
  ): boolean;
  removeCamera(id: string): boolean;
  setActiveCamera(id: string): boolean;

  updateScene(patch: Partial<SceneConfig>): void;
}

export const useDirectorStore = create<DirectorStore>()((set, get) => {
  /**
   * 所有 project 变更的唯一出口：写新 project + 记历史（同目标短时窗内合并为一步）。
   * extra 用于需要同步改动的选择态（如删除后清理 selectedObjectIds）。
   */
  function applyProject(
    nextProject: DirectorProject,
    key: string | null,
    extra?: Partial<DirectorStore>,
  ): void {
    const st = get();
    const now = Date.now();
    const coalesce = key !== null && key === st._lastKey && now - st._lastAt < COALESCE_MS;
    set({
      ...extra,
      project: nextProject,
      _past: pushPast(st._past, st.project, coalesce),
      _future: [],
      _lastKey: key,
      _lastAt: now,
    });
  }

  return {
    project: createEmptyDirectorProject(),
    selectedObjectIds: [],
    selectedCameraId: null,
    viewMode: 'free',
    selectedBoneName: null,
    capturing: false,
    _past: [],
    _future: [],
    _lastKey: null,
    _lastAt: 0,

    setViewMode(mode) {
      set({ viewMode: mode });
    },

    selectBone(name) {
      set({ selectedBoneName: name });
    },

    setCapturing(v) {
      set({ capturing: v });
    },

    undo() {
      const st = get();
      const step = undoStep(st._past, st.project, st._future);
      if (step === null) {
        return;
      }
      // 撤销后清选择：旧 id 可能已不存在，面板对悬空 id 渲染编辑控件会误导
      set({
        project: step.present,
        _past: step.past,
        _future: step.future,
        _lastKey: null,
        selectedObjectIds: [],
        selectedBoneName: null,
      });
    },

    redo() {
      const st = get();
      const step = redoStep(st._past, st.project, st._future);
      if (step === null) {
        return;
      }
      set({
        project: step.present,
        _past: step.past,
        _future: step.future,
        _lastKey: null,
        selectedObjectIds: [],
        selectedBoneName: null,
      });
    },

    setProject(project) {
      const problems = validateDirectorProject(project);
      if (problems.length > 0) {
        console.warn('[directorStore] 拒绝载入不合法工程:', problems);
        return false;
      }
      // 载入/恢复是历史边界：清空 past/future，撤销不该退回载入前的场景
      set({
        project,
        selectedObjectIds: [],
        selectedCameraId: project.activeCameraId,
        viewMode: 'free',
        selectedBoneName: null,
        _past: [],
        _future: [],
        _lastKey: null,
      });
      return true;
    },

    resetProject() {
      set({
        project: createEmptyDirectorProject(),
        selectedObjectIds: [],
        selectedCameraId: null,
        viewMode: 'free',
        selectedBoneName: null,
        _past: [],
        _future: [],
        _lastKey: null,
      });
    },

    addObject(obj) {
      const { project } = get();
      if (project.objects.some((o) => o.id === obj.id)) {
        return false;
      }
      applyProject({ ...project, objects: [...project.objects, obj] }, 'add');
      return true;
    },

    updateObject(id, patch) {
      const { project } = get();
      if (!project.objects.some((o) => o.id === id)) {
        return false;
      }
      const objects = project.objects.map((o) => {
        if (o.id !== id) {
          return o;
        }
        const { transform, ...rest } = patch;
        const next: DirectorObject = { ...o, ...rest };
        if (rest.uniformScale !== undefined) {
          next.uniformScale = clampUniformScale(rest.uniformScale);
        }
        if (transform !== undefined) {
          next.transform = { ...o.transform, ...transform };
        }
        return next;
      });
      applyProject({ ...project, objects }, 'upd:' + id);
      return true;
    },

    removeObject(id) {
      const { project } = get();
      const target = project.objects.find((o) => o.id === id);
      if (target === undefined) {
        return false;
      }
      // 级联：绑定了该角色的跟随机位改为"点位模式"，注视点定格在角色当前位置。
      // 不级联的话 validateDirectorProject 会立刻判悬空引用，工程进入非法态。
      const [x, y, z] = target.transform.position;
      const cameras = project.cameras.map((c) =>
        c.target.mode === 'follow' && c.target.followId === id
          ? { ...c, target: { mode: 'point' as const, point: [x, y, z] as const, followId: null } }
          : c,
      );
      applyProject(
        { ...project, objects: project.objects.filter((o) => o.id !== id), cameras },
        'del:' + id,
        { selectedObjectIds: get().selectedObjectIds.filter((s) => s !== id) },
      );
      return true;
    },

    selectObjects(ids) {
      const { project } = get();
      const valid = new Set(project.objects.map((o) => o.id));
      // 忽略悬空 id：面板对不存在对象渲染编辑控件比不渲染更容易误导用户
      const kept = ids.filter((id) => valid.has(id));
      // 单选制互斥（P0-5b）：选中对象时退出机位选中；反向不清——
      // "跟随选中"徽标需要在选机位时仍知道选中的是谁
      set(kept.length > 0
        ? { selectedObjectIds: kept, selectedCameraId: null, selectedBoneName: null }
        : { selectedObjectIds: kept, selectedBoneName: null });
    },

    addCamera(cam) {
      const { project } = get();
      if (project.cameras.some((c) => c.id === cam.id)) {
        return false;
      }
      const first = project.cameras.length === 0;
      applyProject(
        {
          ...project,
          cameras: [...project.cameras, { ...cam, fov: clampFov(cam.fov) }],
          activeCameraId: first ? cam.id : project.activeCameraId,
        },
        'addcam',
        { selectedCameraId: first ? cam.id : get().selectedCameraId },
      );
      return true;
    },

    selectCamera(id) {
      if (id === null) {
        set({ selectedCameraId: null, selectedBoneName: null });
        return true;
      }
      if (!get().project.cameras.some((c) => c.id === id)) {
        return false;
      }
      // 互斥（第八轮实测：不互斥时角色+机位两套 gizmo 叠在一起）：
      // 选中机位即退出对象/逐骨选中；并强制回自由视角——选中是为了"编辑"，
      // 而相机视角下拖机位会被 CameraDirector 把视口相机搬过去→无限跳远。
      set({ selectedCameraId: id, selectedObjectIds: [], viewMode: 'free', selectedBoneName: null });
      return true;
    },

    updateCamera(id, patch) {
      const { project } = get();
      if (!project.cameras.some((c) => c.id === id)) {
        return false;
      }
      const cameras = project.cameras.map((c) => {
        if (c.id !== id) {
          return c;
        }
        const next: DirectorCamera = { ...c };
        if (patch.name !== undefined) {
          next.name = patch.name;
        }
        if (patch.position !== undefined) {
          next.position = patch.position;
        }
        if (patch.fov !== undefined) {
          next.fov = clampFov(patch.fov);
        }
        if (patch.target !== undefined) {
          next.target = patch.target;
        }
        return next;
      });
      applyProject({ ...project, cameras }, 'cam:' + id);
      return true;
    },

    removeCamera(id) {
      const { project } = get();
      if (!project.cameras.some((c) => c.id === id)) {
        return false;
      }
      const cameras = project.cameras.filter((c) => c.id !== id);
      // 删的是当前取景机位时顺移到下一个，无则置 null——activeCameraId 悬空
      // 会被 validateDirectorProject 判非法，工程保存即报错
      const activeCameraId =
        project.activeCameraId === id
          ? (cameras[0]?.id ?? null)
          : project.activeCameraId;
      applyProject(
        { ...project, cameras, activeCameraId },
        'delcam',
        { selectedCameraId: get().selectedCameraId === id ? null : get().selectedCameraId },
      );
      return true;
    },

    setActiveCamera(id) {
      const { project } = get();
      if (!project.cameras.some((c) => c.id === id)) {
        return false;
      }
      applyProject({ ...project, activeCameraId: id }, 'active:' + id);
      return true;
    },

    updateScene(patch) {
      const { project } = get();
      applyProject({ ...project, scene: { ...project.scene, ...patch } }, 'scene');
    },
  };
});
